import type {ComputedRef, Ref} from 'vue';
import {computed, reactive} from 'vue';

export interface RegisterErrors {
    firstname?: string
    lastname?: string
    gender?: string
    postalCode?: string
    email?: string
    password?: string
    passwordConfirmation?: string
    lgo?: string
    referral?: string
    page?: string
}

interface Values {
    firstname: Ref<string | undefined>
    lastname: Ref<string | undefined>
    gender: Ref<number | undefined>
    postalCode: Ref<string | undefined>
    isEmailValid: ComputedRef<boolean>
    alreadyUsedEmail: Ref<boolean>
    lgo: Ref
    isPasswordValid: ComputedRef<boolean>
    passwordsMatch: ComputedRef<boolean>
    passwordInfo: ComputedRef<string>
    checkedCGU: Ref<boolean>
    checkedTERMS: Ref<boolean>
}

/**
 * Composable to handle validation logic for the registration form,
 * including error management and determining if the form can be submitted.
 */
export function useRegisterValidation(values: Values) {
    const errors = reactive<RegisterErrors>({});

    /**
     * Determine if the form can be submitted by checking that all required fields are filled,
     * valid, and that there are no errors.
     */
    const submitEnabled = computed(() => {
        return !!values.firstname.value?.length &&
            !!values.lastname.value?.length &&
            !!values.postalCode.value?.length &&
            values.gender.value !== undefined &&
            values.isEmailValid.value &&
            !values.alreadyUsedEmail.value &&
            values.lgo.value !== null &&
            values.checkedTERMS.value &&
            values.checkedCGU.value &&
            values.passwordsMatch.value &&
            !errors.referral &&
            values.isPasswordValid.value;
    });

    /**
     * Clear all errors.
     */
    const clearErrors = () => {
        Object.keys(errors).forEach(key => {
            delete errors[key as keyof RegisterErrors];
        });
    };

    /**
     * Run validation rules on the form values and populate the errors object accordingly.
     */
    const runValidationRules = (): boolean => {
        clearErrors();

        if (!values.firstname.value?.length) {
            errors.firstname = 'Prénom requis';
        }
        if (!values.lastname.value?.length) {
            errors.lastname = 'Nom requis';
        }
        if (values.gender.value === undefined) {
            errors.gender = 'Genre requis';
        }
        if (!values.postalCode.value?.length) {
            errors.postalCode = 'Code postal non valide';
        }
        if (!values.isEmailValid.value) {
            errors.email = 'Email non valide';
        }
        if (values.alreadyUsedEmail.value) {
            errors.email = 'Un compte existe déjà avec cet email';
        }
        if (values.lgo.value === null) {
            errors.lgo = 'Le choix de votre lgo est requis';
        }
        if (!values.isPasswordValid.value) {
            errors.password = values.passwordInfo.value;
        } else if (!values.passwordsMatch.value) {
            errors.password = 'Les 2 mots de passe entrés ne correspondent pas';
        }
        if (!values.checkedTERMS.value) {
            errors.page = 'Veuillez accepter la politique de confidentialité des données pour continuer';
        }
        if (!values.checkedCGU.value) {
            errors.page = 'Veuillez accepter les CGU pour continuer';
        }

        return Object.keys(errors).length === 0;
    };

    return {errors, submitEnabled, runValidationRules};
}
